<template>
  <div class="gpct-body">
    <div class="gpct-header">
      <div>
        <p class="gpct-header__title">Group Promo Codes</p>
        <p class="gpct-header__sub">Promo codes that work across all stores in the group</p>
      </div>
      <hs-button v-if="canManagePromoCodes" variant="primary" icon="plus" @click="showCreateModal = true">
        Create Promo Code
      </hs-button>
    </div>

    <div v-if="promoCodes.length > 0 || searchQuery || statusFilter" class="gpct-filters">
      <hs-input v-model="searchQuery" icon="search" placeholder="Search by code…" label="Search" />
      <hs-select v-model="statusFilter" label="Status" placeholder="All" :options="statusOptions" />
      <div class="gpct-filters__clear">
        <hs-button variant="ghost" icon="x" @click="clearFilters">Clear</hs-button>
      </div>
    </div>

    <div v-if="loading" class="gpct-state">
      <span class="gpct-spinner" />Loading promo codes…
    </div>

    <hs-alert v-else-if="error" tone="danger" :message="error">
      <template #default>
        {{ error }}
        <hs-button variant="ghost" size="sm" @click="loadPromoCodes">Retry</hs-button>
      </template>
    </hs-alert>

    <hs-empty-state
      v-else-if="promoCodes.length === 0 && !searchQuery && !statusFilter"
      icon="tag"
      title="No promo codes yet"
      description="Create promo codes that work across all group stores."
    />

    <hs-empty-state
      v-else-if="promoCodes.length === 0"
      icon="search"
      title="No results"
      description="No promo codes match your filters."
    />

    <div v-else class="gpct-table-area">
      <hs-table-shell title="Group promo codes" description="Promo codes available across stores in this group.">
        <hs-data-table
          :headers="promoHeaders"
          :data="promoCodes"
          :header-key-mapping="promoHeaderKeyMapping"
        >
          <template #cell-code="{ row }">
            <span class="gpct-code">{{ row.code }}</span>
          </template>

          <template #cell-status="{ row }">
            <hs-badge :tone="getStatusTone(row)" size="sm">{{ getStatus(row) }}</hs-badge>
          </template>

          <template #cell-discount="{ row }">
            {{ formatDiscount(row) }}
          </template>

          <template #cell-expiration="{ row }">
            {{ formatDate(row.expiration_date) }}
          </template>

          <template #cell-usage="{ row }">
            {{ formatUsage(row) }}
          </template>

          <template #cell-actions="{ row }">
            <div class="gpct-table-actions">
              <hs-button variant="secondary" icon="edit-2" size="sm" @click="editPromoCode(row)">
                Edit
              </hs-button>
              <hs-button
                :variant="row.is_active ? 'danger' : 'success'"
                :icon="row.is_active ? 'toggle-right' : 'toggle-left'"
                size="sm"
                @click="toggleActiveStatus(row)"
              >
                {{ row.is_active ? "Deactivate" : "Activate" }}
              </hs-button>
            </div>
          </template>
        </hs-data-table>
      </hs-table-shell>

      <hs-pagination
        v-if="pagination.total > 0"
        :current-page="pagination.page"
        :total-pages="pagination.pages"
        :total-items="pagination.total"
        :items-per-page="pagination.perPage"
        @previous-page="handlePreviousPage"
        @next-page="handleNextPage"
        @update:itemsPerPage="handlePerPageChange"
      />
    </div>

    <hs-confirm-dialog
      v-model="showToggleConfirm"
      :title="codeToToggle?.is_active ? 'Deactivate Promo Code' : 'Activate Promo Code'"
      :message="codeToToggle ? `Are you sure you want to ${codeToToggle.is_active ? 'deactivate' : 'activate'} the promo code '${codeToToggle.code}'?` : ''"
      :confirm-text="codeToToggle?.is_active ? 'Deactivate' : 'Activate'"
      cancel-text="Cancel"
      :tone="codeToToggle?.is_active ? 'danger' : 'primary'"
      :loading="toggling"
      @confirm="confirmToggle"
    />

    <create-group-promo-code-modal
      v-if="showCreateModal"
      :group-id="group.id"
      @close="showCreateModal = false"
      @created="onPromoCodeCreated"
    />

    <edit-group-promo-code-modal
      v-if="codeToEdit"
      :group-id="group.id"
      :promo-code="codeToEdit"
      @close="codeToEdit = null"
      @updated="onPromoCodeUpdated"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import CreateGroupPromoCodeModal from "@/components/settings/group/CreateGroupPromoCodeModal.vue";
import EditGroupPromoCodeModal from "@/components/settings/group/EditGroupPromoCodeModal.vue";
import { HsButton, HsInput, HsSelect, HsBadge, HsAlert, HsEmptyState, HsConfirmDialog, HsPagination, HsDataTable, HsTableShell } from "@/components/ui";

export default {
  name: "GroupPromoCodesTab",
  components: {
    CreateGroupPromoCodeModal, EditGroupPromoCodeModal,
    HsButton, HsInput, HsSelect, HsBadge, HsAlert, HsEmptyState, HsConfirmDialog, HsPagination, HsDataTable, HsTableShell,
  },
  props: {
    group: { type: Object, required: true },
  },
  emits: ["refresh"],
  setup(props) {
    const store = useStore();
    const loading = ref(false);
    const error = ref("");
    const promoCodes = ref([]);
    const showCreateModal = ref(false);
    const codeToToggle = ref(null);
    const showToggleConfirm = ref(false);
    const toggling = ref(false);
    const codeToEdit = ref(null);
    const searchQuery = ref("");
    const statusFilter = ref("");

    const pagination = ref({ page: 1, perPage: 10, total: 0, pages: 0, hasNext: false, hasPrev: false });
    const promoHeaders = ["Code", "Status", "Discount", "Expiration", "Usage", "Actions"];
    const promoHeaderKeyMapping = {
      Code: "code",
      Status: "status",
      Discount: "discount",
      Expiration: "expiration_date",
      Usage: "usage",
      Actions: "actions",
    };

    const statusOptions = computed(() => [
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
      { label: "Expired", value: "expired" },
    ]);

    const canManagePromoCodes = computed(() =>
      store.getters["groups/canManagePromoCodes"] || store.getters["groups/isGroupOwner"]
    );

    const loadPromoCodes = async () => {
      loading.value = true;
      error.value = "";
      try {
        const params = { page: pagination.value.page, per_page: pagination.value.perPage };
        if (searchQuery.value) { params.search_query = searchQuery.value; params.search_by = "code"; }
        if (statusFilter.value === "active") params.is_active = true;
        else if (statusFilter.value === "inactive") params.is_active = false;
        else if (statusFilter.value === "expired") params.expired = true;

        const response = await http.get(`/v2/groups/${props.group.id}/promo-codes`, { params });
        const data = response.data || {};
        promoCodes.value = data.items || [];
        pagination.value = {
          page: data.page || 1,
          perPage: data.per_page || 10,
          total: data.total || 0,
          pages: data.pages || 0,
          hasNext: data.has_next || false,
          hasPrev: data.has_prev || false,
        };
      } catch (err) {
        console.error("Failed to load promo codes", err);
        error.value = err?.response?.data?.error?.message || "Unable to load promo codes.";
      } finally {
        loading.value = false;
      }
    };

    const handlePreviousPage = () => { if (pagination.value.hasPrev) { pagination.value.page -= 1; loadPromoCodes(); } };
    const handleNextPage = () => { if (pagination.value.hasNext) { pagination.value.page += 1; loadPromoCodes(); } };
    const handlePerPageChange = (perPage) => { pagination.value.perPage = perPage; pagination.value.page = 1; loadPromoCodes(); };
    const clearFilters = () => { searchQuery.value = ""; statusFilter.value = ""; pagination.value.page = 1; };

    const toggleActiveStatus = (code) => {
      codeToToggle.value = code;
      showToggleConfirm.value = true;
    };

    const confirmToggle = async () => {
      if (!codeToToggle.value) return;
      toggling.value = true;
      try {
        await http.patch(`/v2/groups/${props.group.id}/promo-codes/${codeToToggle.value.id}`, {
          is_active: !codeToToggle.value.is_active,
        });
        toast.success(`Promo code ${codeToToggle.value.is_active ? "deactivated" : "activated"} successfully`);
        showToggleConfirm.value = false;
        codeToToggle.value = null;
        await loadPromoCodes();
      } catch (err) {
        console.error("Failed to toggle promo code status", err);
        toast.error(err?.response?.data?.error?.message || "Unable to update promo code status.");
      } finally {
        toggling.value = false;
      }
    };

    const getStatus = (code) => {
      if (!code.is_active) return "Inactive";
      if (code.expiration_date && new Date(code.expiration_date) < new Date()) return "Expired";
      if (code.usage_limit && (code.usage_count || 0) >= code.usage_limit) return "Depleted";
      return "Active";
    };

    const getStatusTone = (code) => {
      const status = getStatus(code);
      const tones = { Active: "success", Inactive: "neutral", Expired: "danger", Depleted: "warning" };
      return tones[status] || "neutral";
    };

    const formatDiscount = (code) => {
      if (code.discount_percentage) return `${parseFloat(code.discount_percentage)}% off`;
      if (code.discount_amount) return `$${parseFloat(code.discount_amount).toFixed(2)} off`;
      return "N/A";
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Never";
      return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const formatUsage = (code) => {
      if (!code.usage_limit) return "Unlimited";
      return `${code.usage_count || 0} / ${code.usage_limit} uses`;
    };

    const editPromoCode = (code) => { codeToEdit.value = code; };
    const onPromoCodeCreated = async () => { showCreateModal.value = false; await loadPromoCodes(); };
    const onPromoCodeUpdated = async () => { codeToEdit.value = null; await loadPromoCodes(); };

    watch([searchQuery, statusFilter], () => { pagination.value.page = 1; loadPromoCodes(); });
    onMounted(() => { loadPromoCodes(); });

    return {
      loading, error, promoCodes, showCreateModal, codeToToggle, showToggleConfirm, toggling, codeToEdit,
      searchQuery, statusFilter, pagination, statusOptions, canManagePromoCodes,
      promoHeaders, promoHeaderKeyMapping,
      loadPromoCodes, handlePreviousPage, handleNextPage, handlePerPageChange, clearFilters,
      toggleActiveStatus, confirmToggle, editPromoCode, onPromoCodeCreated, onPromoCodeUpdated,
      getStatus, getStatusTone, formatDiscount, formatDate, formatUsage,
    };
  },
};
</script>

<style scoped>
.gpct-body {
  display: grid;
  gap: 1.1rem;
}

.gpct-header {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
}

.gpct-header__title {
  color: var(--hs-text);
  font-size: 0.95rem;
  font-weight: 750;
  margin: 0;
}

.gpct-header__sub {
  color: var(--hs-muted);
  font-size: 0.8rem;
  margin: 0.15rem 0 0;
}

.gpct-filters {
  align-items: flex-end;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr auto;
}

.gpct-filters__clear {
  padding-bottom: 0.1rem;
}

@media (max-width: 520px) {
  .gpct-filters { grid-template-columns: 1fr; }
}

.gpct-state {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.875rem;
  gap: 0.65rem;
  justify-content: center;
  padding: 2.5rem 0;
}

.gpct-spinner {
  animation: gpct-spin 0.8s linear infinite;
  border: 2px solid color-mix(in srgb, var(--hs-primary) 28%, transparent);
  border-radius: 50%;
  border-top-color: var(--hs-primary);
  display: inline-block;
  height: 1.1rem;
  width: 1.1rem;
}

@keyframes gpct-spin { to { transform: rotate(360deg); } }

.gpct-table-area {
  display: grid;
  gap: 0.85rem;
}

.gpct-code {
  color: var(--hs-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 0.86rem;
  font-weight: 850;
  letter-spacing: 0.06em;
}

.gpct-table-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
</style>
